<template>
  <div>
    <div>
      <AIDisclaimer
        v-if="!goalsRequestFinished"
        :single-line="true"
        class="ai-disclaimer"/>
      <section v-if="!goalRecommendationsFailed">
        <div
          class="aligned-goals-results-container"
          v-for="(skills, subject) in allDraftedSkills"
          :key="subject">
          <h2 class="subject-header">
            {{ getPlAreaLabelFromValue(subject) }} <span class="browse-goals-subheader" v-if="!currentlyLoadingGoalRecommendations">- Browse more content in <router-link
              @click.native="sendBrowseEvent(subject, getBrowseGradeFilters(subject, gradeForBrowse))"
              class="browse-goals-link"
              :to="{name: 'BrowseGoalsPage', query: {subject: returnBrowseSubject(subject), grades: getBrowseGradeFilters(subject, gradeForBrowse), ref:'plw'}}"
              target="_blank">{{ getPlAreaLabelFromValue(subject) }} <OpensInNewTabIcon /></router-link>
            </span>
          </h2>
          <section
            v-for="(skill, idx) in skills"
            :key="`${subject}-${skill}-${idx}`"
            class="row category-section">
            <template v-if="!currentlyLoadingGoalRecommendations">
              <h3 class="recommendation-header">
                <span class="subject-label-for-goal">{{ skill }}</span>
              </h3>
              <p v-if="isDraftedStrength(subject, skill)" class='strength-skill-message'>
                This area was selected as a strength. For aligned goals, edit the statement for an area of need.
              </p>
              <p v-else-if="!recommendedGoalsForSkill(subject, skill).length" class="no-goals-in-subject">
                No aligned goals found.
              </p>
              <template
                v-else
                v-for="(goal, index) in recommendedGoalsForSkill(subject, skill)">
                <div :key="goal.uuid" class="col-md-3 no-padding">
                  <GoalCardContainer
                    :position="index"
                    :goal="goal"
                    :restriction-type="goal.restrictionType"
                    :standard-set-label="standardSetLabelForSubjectCard(getGoalSubjectLabelFromValue(subject))"
                    :standard-set-for-ref-display="dropdownStandardSetForSubject(getGoalSubjectLabelFromValue(subject))"
                    :aligned-goals-tile="true" />
                </div>
              </template>
            </template>
            <div class="col-md-12 loading-skeleton">
              <PresentLevelsLoadingGoalCards v-if="currentlyLoadingGoalRecommendations"/>
            </div>
          </section>
        </div>
      </section>
      <div v-if="goalRecommendationsFailed" class="alert alert-info no-goals-banner">
        <span class="material-icons-round info-circle">info_outline</span>
        We're not able to retrieve aligned goals right now. Try again later. To view more content,
        <router-link
          class="browse-goals-link"
          :to="{name: 'BrowseGoalsPage', query: {ref:'plw'}}"
          target="_blank">
          Browse Goals <OpensInNewTabIcon />
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import GoalCardContainer from '@/components/search/goalcard/GoalCardContainer'
import PresentLevelsLoadingGoalCards from '@/components/presentlevels/PresentLevelsLoadingGoalCards'
import { noSelMixin } from '@/contentprofile/no_sel_mixins'
import AIDisclaimer from '@/components/AIDisclaimer.vue';
import { subjectHasGradeFiltersForAlignedGoals } from '@/api/presentlevels'
import OpensInNewTabIcon from '../OpensInNewTabIcon.vue';

const PLWSubjectToBrowseSubject = {
  'Pre-Academic Skills':  'Pre-Academics',
  'Reading': 'Reading',
  'Writing': 'Writing',
  'Math': 'Math',
  'Alt Academics: Reading': 'Alt Academics',
  'Alt Academics: Writing': 'Alt Academics',
  'Alt Academics: Math':  'Alt Academics',
  'Executive Functioning': 'Executive Functioning',
  'Behavior':  'Behavior & SEL',
  'Social & Emotional Learning':  'Behavior & SEL',
  'Communication': 'Speech & Language',
  'Fine Motor Skills': 'Fine Motor',
  'Gross Motor Skills':  'Gross Motor',
  'Functional':  'Life Skills',
  'Vocational Skills': 'Transition'
}

export default {
  name: 'GoalsShortcuts',
  components: {
    GoalCardContainer,
    PresentLevelsLoadingGoalCards,
    AIDisclaimer,
    OpensInNewTabIcon
  },
  mixins: [noSelMixin],
  props: {
    altHeader: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      sortedRecommendations: {}
    }
  },
  mounted() {
    this.groupRecommendationsBySubject(this.goalRecommendations)
  },
  computed: {
    ...mapGetters({
      filters: 'browse/filters',
      dropdownStandardSetForSubject: 'contentProfile/dropdownStandardSetForSubject',
      standardSetLabelForSubjectCard: 'contentProfile/standardSetLabelForSubjectCard',
      contentProfileSubjects: 'contentProfile/publishedSubjects'
    }),
    ...mapGetters('presentlevels', ['draftedStrengths']),
    ...mapState('presentlevels', {
      goalRecommendations: state => {
        return state.goalRecommendations
      },
      currentlyLoadingGoalRecommendations: state => {
        return state.loadingRecommendations
      },
      goalsRequestFinished: state => {
        return state.recommendationsRetrieved
      },
      gradeForBrowse: state => {
        return state.gradeForBrowse
      },
      draftedSkills: state => {
        return state.draftedSkills
      },
      goalRecommendationsFailed: state => {
        return state.goalRecommendationsFailed
      }
    }),
    allDraftedSkills() {
      const result = {}
      const strengths = this.draftedSkills?.strengths || {}
      const needs      = this.draftedSkills?.needs      || {}

      for (const [subject, skills] of Object.entries(strengths)) {
        result[subject] = [...new Set(skills)]
      }

      for (const [subject, skills] of Object.entries(needs)) {
        result[subject] = [...new Set([...(result[subject] || []), ...skills])]
      }

      return result
    },
    emptyData() {
      return Object.keys(this.goalsShortcutData).length === 0
    },
    subjectGoalCountsMap() {
      let map = {}
      let totalGoals = 0
      for (var subject in this.sortedRecommendations) {
        let goals = []
        this.sortedRecommendations[subject].forEach(recs => {
          goals = goals.concat(recs.goals)
        })
        map[subject] = goals.length
        totalGoals += goals.length
      }
      map.totalGoals = totalGoals
      return map
    }
  },
  methods: {
    sendBrowseEvent(subject, grades) {
      this.$sendEvent('Clicked Present Levels Goal Shortcut', {
        plw_goal_shortcut_subject: subject,
        plw_goal_shortcut_grades: grades,
        source: 'aligned goals'
      })
    },
    getBrowseGradeFilters(subject, studentGrade) {
      if (!subjectHasGradeFiltersForAlignedGoals(subject) || subject === 'Vocational Skills') {
        return []
      }
      return [studentGrade]
    },
    returnBrowseSubject(subject) {
      return this.getGoalSubjectLabelFromValue(PLWSubjectToBrowseSubject[subject]) || subject
    },
    groupRecommendationsBySubject(recommendations) {
      this.sortedRecommendations = {}
      for (var i = 0; i < recommendations.length; i++) {
        let rec = recommendations[i]
        if (!Array.isArray(this.sortedRecommendations[rec.subject])) {
          this.sortedRecommendations[rec.subject] = [rec]
        } else {
          this.sortedRecommendations[rec.subject].push(rec)
        }
      }
    },
    recommendedGoalsForSkill(subject, skill) {
      const categoriesForSubject = this.sortedRecommendations[subject]
      if (!categoriesForSubject || !categoriesForSubject.length) {
        return []
      }
      const recommendationsForSkills = categoriesForSubject.find(category => category.category === skill)
      return recommendationsForSkills?.goals || []
    },
    isDraftedStrength(subject, skill) {
      if (!this.draftedStrengths[subject]) {
        return false
      }
      return this.draftedStrengths[subject].includes(skill)
    }
  },
  watch: {
    goalRecommendations(to) {
      this.groupRecommendationsBySubject(to)
    }
  },
}
</script>

<style lang="less" scoped>
@import '~@/variables.less';
.no-padding {
  padding-left: 4px;
  padding-right: 4px;
  margin-bottom: var(--spacing-xl);
}
.subject-label-for-goal {
  font-size: 20px;
  font-weight: 400;
}
.recommendation-header {
  font-size: 24px;
  margin-top: 0;
  margin-bottom: 8px;
  font-weight: 500;
}
.browse-goals-link:hover {
  text-decoration: underline;
}
.browse-goals-link {
  font-weight: 400;
  font-size: 18px;
  i {
    font-size: 16px;
  }
}
.category-section {
  margin-left: 0;
  margin-bottom: 24px;
}
.subject-header {
  margin-top: 0;
  margin-bottom: 12px;
  font-weight: 500;
}
.aligned-goals-results-container {
  margin-bottom: 48px;
}
.browse-goals-subheader {
  font-size: 20px;
  font-weight: 400;
}
.no-goals-in-category {
  margin-bottom: 48px;
  font-size: 16px;
}
.no-goals-in-subject, .strength-skill-message {
  margin-bottom: -12px;
  font-size: 16px;
}
.no-goals-banner {
  margin: 1rem 0;
  padding: 8px;
  max-width: 720px;
  font-size: 14px;
  border-radius: 4px;
  color: black;
}
.ai-warning {
  margin-bottom: 36px;
}
.info-circle {
  font-size: 16px;
  color: @gb_blue_medium;
  vertical-align: text-bottom;
}
.loading-skeleton {
  padding-left: 0;
}
.ai-disclaimer {
  margin-bottom: 16px;
}

</style>
